// ui/insightModal.js
// Modal for using Ritual Insight items - select a skill to receive XP

import { gameState } from '../game/gameState.js';
import { SKILLS, SKILL_CATEGORIES, getSkillCategory } from '../game/constants.js';
import { skillRegistry } from '../core/skillRegistry.js';
import { getItem, getItemName, getItemIcon } from '../data/items/index.js';
import { addXP } from '../systems/experience.js';
import { formatNumber } from '../utils/format.js';
import { toastSuccess, toastWarning } from './toast.js';
import logger from '../utils/logger.js';
import { checkPyreLoreTriggers, showPyreVeilLoreQueue } from './pyreVeilLoreModal.js';

let modalElement = null;
let currentInsightId = null;
let selectedInsightQty = 1; // 'all' or a number

/**
 * Get all trainable skills with their current levels
 * @returns {Array} Array of { id, name, iconHtml, level }
 */
function getTrainableSkills() {
    const skills = [];

    for (const [key, skillId] of Object.entries(SKILLS)) {
        const skillData = skillRegistry.get(skillId);
        if (!skillData) continue;

        const playerSkill = gameState.getSkill(skillId);
        if (!playerSkill) continue;

        skills.push({
            id: skillId,
            name: skillData.name,
            iconHtml: skillRegistry.getSkillIcon(skillId),
            level: playerSkill.level
        });
    }

    // Sort alphabetically by name
    return skills.sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Check if a skill is allowed by the insight's category restrictions
 * @param {string} skillId - The skill ID to check
 * @param {Array} allowedCategories - Array of allowed category IDs (e.g., ['combat', 'gathering'])
 * @returns {boolean} True if the skill is allowed
 */
function isSkillAllowedByCategory(skillId, allowedCategories) {
    // If no category restrictions, allow all skills (universal insight)
    if (!allowedCategories || allowedCategories.length === 0) {
        return true;
    }

    const skillCategory = getSkillCategory(skillId);
    if (!skillCategory) {
        return false;
    }

    return allowedCategories.includes(skillCategory.id);
}

/**
 * Get all skills in the allowed categories
 * @param {Array} allowedCategories - Array of allowed category IDs
 * @returns {Array} Array of skill IDs in those categories
 */
function getSkillsInCategories(allowedCategories) {
    if (!allowedCategories || allowedCategories.length === 0) {
        return Object.values(SKILLS);
    }

    const skills = [];
    for (const catId of allowedCategories) {
        const category = Object.values(SKILL_CATEGORIES).find(c => c.id === catId);
        if (category) {
            skills.push(...category.skills);
        }
    }
    return skills;
}

/**
 * Show the insight usage modal
 * @param {string} insightId - The insight item ID
 * @param {Object} [options] - Options
 * @param {boolean} [options.useAll] - If true, use all insights at once (shift+click for single-skill categories)
 */
export function showInsightModal(insightId, options = {}) {
    const item = getItem(insightId);
    if (!item || !item.xpAmount) {
        toastWarning('Invalid insight item');
        return;
    }

    // Check if player has the insight
    const count = gameState.getItemCount(insightId);
    if (count <= 0) {
        toastWarning('You don\'t have this insight');
        return;
    }

    currentInsightId = insightId;
    const skills = getTrainableSkills();
    const minLevel = item.minLevel || 1;
    const xpAmount = item.xpAmount;
    const itemIcon = getItemIcon(insightId);
    const itemName = getItemName(insightId);
    const allowedCategories = item.allowedCategories || null;

    // Auto-apply for single-skill categories (e.g., Combat insights)
    if (allowedCategories && allowedCategories.length === 1) {
        const categoryId = allowedCategories[0];
        const category = Object.values(SKILL_CATEGORIES).find(c => c.id === categoryId);

        if (category && category.skills.length === 1) {
            const targetSkillId = category.skills[0];
            const playerSkill = gameState.getSkill(targetSkillId);

            // Verify level requirement (no max level cap - virtual levels go to 120)
            if (playerSkill && playerSkill.level >= minLevel) {
                // Shift+click uses all, normal click uses 1
                const useQty = options.useAll ? count : 1;

                // Directly use the insight without showing modal
                const removed = gameState.removeItem(insightId, useQty);
                if (removed) {
                    const totalXP = item.xpAmount * useQty;
                    const oldLevel = playerSkill.level;
                    const xpResult = addXP(targetSkillId, totalXP, null, { skipCorruption: true });
                    const skillData = skillRegistry.get(targetSkillId);
                    const skillName = skillData ? skillData.name : targetSkillId;

                    const qtyLabel = useQty > 1 ? `${useQty}x ` : '';
                    if (xpResult.leveledUp) {
                        toastSuccess(`Used ${qtyLabel}${itemName}! ${skillName} gained ${formatNumber(totalXP)} XP and reached level ${xpResult.newLevel}!`);

                        // Check for Pyre Veil lore triggers across the full level range
                        if (targetSkillId === SKILLS.PYRE) {
                            const loreTriggers = checkPyreLoreTriggers(oldLevel, xpResult.newLevel);
                            if (loreTriggers.length > 0) {
                                showPyreVeilLoreQueue(loreTriggers);
                            }
                        }
                    } else {
                        toastSuccess(`Used ${qtyLabel}${itemName}! ${skillName} gained ${formatNumber(totalXP)} XP.`);
                    }

                    logger.debug(`[INSIGHT] Auto-applied ${useQty}x ${insightId} to ${targetSkillId}, granted ${totalXP} XP`);
                    return;
                }
            } else if (playerSkill && playerSkill.level < minLevel) {
                toastWarning(`Requires level ${minLevel} in ${category.name}`);
                return;
            }
        }
    }

    // Build skill grid - show all skills but disable those not in allowed categories
    // Note: No max level restriction - virtual levels go to 120, insights always usable
    let skillsHTML = '';
    for (const skill of skills) {
        const meetsRequirement = skill.level >= minLevel;
        const isAllowedCategory = isSkillAllowedByCategory(skill.id, allowedCategories);

        // Skill is disabled if: doesn't meet level req OR not in allowed category
        const disabled = !meetsRequirement || !isAllowedCategory;
        const disabledClass = disabled ? 'disabled' : '';
        const lockedClass = !isAllowedCategory ? 'category-locked' : '';

        // Determine the reason for being disabled
        let disabledReason = '';
        if (!isAllowedCategory) {
            disabledReason = '🔒';
        } else if (!meetsRequirement) {
            disabledReason = `Req: Lv ${minLevel}`;
        }

        skillsHTML += `
            <div class="insight-skill-option ${disabledClass} ${lockedClass}"
                 data-skill-id="${skill.id}"
                 ${disabled ? '' : `onclick="window.insightModal.selectSkill('${skill.id}')"`}>
                <span class="skill-icon">${skill.iconHtml}</span>
                <span class="skill-name">${skill.name}</span>
                <span class="skill-level">Lv ${skill.level}</span>
                ${disabledReason ? `<span class="skill-disabled-reason">${disabledReason}</span>` : ''}
            </div>
        `;
    }

    const html = `
        <div class="modal-overlay insight-modal-overlay" id="insight-modal-overlay">
            <div class="modal-content insight-modal">
                <div class="modal-header">
                    <h2>Use ${itemName}</h2>
                    <button class="modal-close" onclick="window.insightModal.close()">&times;</button>
                </div>
                <div class="modal-body">
                    <div class="insight-info">
                        <span class="insight-icon">${itemIcon}</span>
                        <div class="insight-details">
                            <p class="insight-xp">Grants <strong>${formatNumber(xpAmount)} XP</strong></p>
                            <p class="insight-desc">${item.description || ''}</p>
                            <p class="insight-count">You have: ${count}</p>
                        </div>
                    </div>
                    <div class="insight-quantity-selector">
                        <span class="insight-qty-label">Qty:</span>
                        <div class="insight-qty-presets">
                            <button class="insight-qty-btn active" data-qty="1">1</button>
                            <button class="insight-qty-btn" data-qty="5">5</button>
                            <button class="insight-qty-btn" data-qty="10">10</button>
                            <button class="insight-qty-btn" data-qty="all">All</button>
                        </div>
                        <div class="insight-qty-custom">
                            <input type="number" class="insight-qty-input" min="1" placeholder="X">
                            <button class="insight-qty-set-btn">Set</button>
                        </div>
                    </div>
                    <p class="insight-prompt">Select a skill to receive the insight:</p>
                    <div class="insight-skill-grid">
                        ${skillsHTML}
                    </div>
                </div>
            </div>
        </div>
    `;

    // Remove existing modal if any
    if (modalElement) {
        modalElement.remove();
    }

    // Create and append modal
    modalElement = document.createElement('div');
    modalElement.innerHTML = html;
    document.body.appendChild(modalElement);

    // Reset quantity to 1 on each modal open
    selectedInsightQty = 1;

    // Wire up quantity selector buttons
    const qtyPresets = modalElement.querySelector('.insight-qty-presets');
    if (qtyPresets) {
        qtyPresets.addEventListener('click', (e) => {
            const btn = e.target.closest('.insight-qty-btn');
            if (!btn) return;
            const val = btn.dataset.qty;
            setInsightQuantity(val === 'all' ? 'all' : parseInt(val, 10));
        });
    }

    const qtySetBtn = modalElement.querySelector('.insight-qty-set-btn');
    const qtyInput = modalElement.querySelector('.insight-qty-input');
    if (qtySetBtn && qtyInput) {
        qtySetBtn.addEventListener('click', () => {
            const val = parseInt(qtyInput.value, 10);
            if (val > 0) {
                setInsightQuantity(val);
            }
        });
        qtyInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                const val = parseInt(qtyInput.value, 10);
                if (val > 0) {
                    setInsightQuantity(val);
                }
            }
        });
    }

    // Add fade-in animation
    requestAnimationFrame(() => {
        const overlay = document.getElementById('insight-modal-overlay');
        if (overlay) overlay.classList.add('active');
    });

    // Close on overlay click (outside modal)
    const overlay = document.getElementById('insight-modal-overlay');
    if (overlay) {
        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) {
                closeInsightModal();
            }
        });
    }

    // Close on Escape key
    document.addEventListener('keydown', handleKeydown);
}

function handleKeydown(e) {
    if (e.key === 'Escape') {
        closeInsightModal();
    }
}

/**
 * Close the insight modal
 */
export function closeInsightModal() {
    document.removeEventListener('keydown', handleKeydown);

    const overlay = document.getElementById('insight-modal-overlay');
    if (overlay) {
        overlay.classList.remove('active');
        overlay.classList.add('closing');

        // Wait for animation then remove
        setTimeout(() => {
            if (modalElement) {
                modalElement.remove();
                modalElement = null;
            }
        }, 200);
    }

    currentInsightId = null;
}

/**
 * Set the quantity for bulk insight usage
 * @param {number|string} qty - Number or 'all'
 */
function setInsightQuantity(qty) {
    selectedInsightQty = qty;

    // Update active state on preset buttons
    if (modalElement) {
        const buttons = modalElement.querySelectorAll('.insight-qty-btn');
        buttons.forEach(btn => {
            const btnVal = btn.dataset.qty;
            const isActive = (qty === 'all' && btnVal === 'all') || (btnVal === String(qty));
            btn.classList.toggle('active', isActive);
        });

        // Clear custom input if a preset was clicked
        const input = modalElement.querySelector('.insight-qty-input');
        if (input && ['1', '5', '10', 'all'].includes(String(qty))) {
            input.value = '';
        }
    }
}

/**
 * Use the insight on the selected skill
 * @param {string} skillId - The skill to grant XP to
 */
export function selectSkill(skillId) {
    if (!currentInsightId) {
        toastWarning('No insight selected');
        closeInsightModal();
        return;
    }

    const item = getItem(currentInsightId);
    if (!item || !item.xpAmount) {
        toastWarning('Invalid insight');
        closeInsightModal();
        return;
    }

    // Verify player still has the insight
    const count = gameState.getItemCount(currentInsightId);
    if (count <= 0) {
        toastWarning('You no longer have this insight');
        closeInsightModal();
        return;
    }

    // Verify skill is in allowed category (for locked insights)
    if (!isSkillAllowedByCategory(skillId, item.allowedCategories)) {
        const skillCategory = getSkillCategory(skillId);
        const categoryName = skillCategory ? skillCategory.name : 'this skill';
        toastWarning(`This insight cannot be used on ${categoryName} skills`);
        return;
    }

    // Verify skill level requirement
    const playerSkill = gameState.getSkill(skillId);
    if (!playerSkill) {
        toastWarning('Invalid skill');
        closeInsightModal();
        return;
    }

    const minLevel = item.minLevel || 1;
    if (playerSkill.level < minLevel) {
        toastWarning(`Requires level ${minLevel} in this skill`);
        return;
    }

    // No max level restriction - virtual levels go to 120, insights always usable

    // Calculate effective quantity
    const available = gameState.getItemCount(currentInsightId);
    const effectiveQty = selectedInsightQty === 'all' ? available : Math.min(selectedInsightQty, available);

    if (effectiveQty <= 0) {
        toastWarning('You no longer have this insight');
        closeInsightModal();
        return;
    }

    // Remove insights from inventory (single call)
    const removed = gameState.removeItem(currentInsightId, effectiveQty);
    if (!removed) {
        toastWarning('Failed to use insight');
        closeInsightModal();
        return;
    }

    // Grant XP in a single call (multiplied amount)
    const totalXP = item.xpAmount * effectiveQty;
    const oldLevel = playerSkill.level;
    const xpResult = addXP(skillId, totalXP, null, { skipCorruption: true });

    // Get skill display info
    const skillData = skillRegistry.get(skillId);
    const skillName = skillData ? skillData.name : skillId;
    const itemName = getItemName(currentInsightId);

    // Show summary toast
    const qtyLabel = effectiveQty > 1 ? `${effectiveQty}x ` : '';
    if (xpResult.leveledUp) {
        toastSuccess(`Used ${qtyLabel}${itemName}! ${skillName} gained ${formatNumber(totalXP)} XP and reached level ${xpResult.newLevel}!`);

        // Check for Pyre Veil lore triggers across the full level range
        if (skillId === SKILLS.PYRE) {
            const loreTriggers = checkPyreLoreTriggers(oldLevel, xpResult.newLevel);
            if (loreTriggers.length > 0) {
                closeInsightModal();
                showPyreVeilLoreQueue(loreTriggers);
                return;
            }
        }
    } else {
        toastSuccess(`Used ${qtyLabel}${itemName}! ${skillName} gained ${formatNumber(totalXP)} XP.`);
    }

    logger.debug(`[INSIGHT] Used ${effectiveQty}x ${currentInsightId} on ${skillId}, granted ${totalXP} XP`);

    closeInsightModal();
}

/**
 * Check if an item is a usable insight
 * @param {string} itemId - The item ID to check
 * @returns {boolean} True if the item is a Ritual Insight
 */
export function isInsightItem(itemId) {
    const item = getItem(itemId);
    return item && item.category === 'insight' && item.usable && item.xpAmount > 0;
}

// Expose to window for onclick handlers
window.insightModal = {
    selectSkill,
    close: closeInsightModal,
    setInsightQuantity
};

export default {
    showInsightModal,
    closeInsightModal,
    selectSkill,
    isInsightItem
};
